import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { collection, query, getDocs, where, limit, doc, onSnapshot } from 'firebase/firestore';
import ModalEmision from '../../../ui/ModalEmision';
import ModalAnulacion from '../../../ui/ModalAnulacion';
import EditarRenovacion from '../../../ui/EditarRenovacion'; 
import { getMotivoChipClass, getPersonaChipClass, getEstadoChipClass } from '../../../../utils/styles';

/**
 * DetalleTab - Visualización de la ficha técnica de la póliza patrimonial.
 * Actualización en tiempo real integrada para evitar recargas manuales.
 */
const DetalleTab = ({ poliza, client, onUpdate }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);
    const [showEditarRenovacionModal, setShowEditarRenovacionModal] = useState(false); 
    
    // --- ESTADO PARA TIEMPO REAL ---
    const [localPoliza, setLocalPoliza] = useState(poliza);
    const [isValidating, setIsValidating] = useState(poliza?.riesgo?.toUpperCase().includes('CAUCI'));
    const [hasRefacturaciones, setHasRefacturaciones] = useState(false);

    // 1. LISTENER DE FIRESTORE (SINCRONIZACIÓN INMEDIATA)
    useEffect(() => {
        const pId = poliza?.id || localPoliza?.id;
        const cId = client?.id || poliza?.clienteId || localPoliza?.clienteId;

        if (!pId || !cId) return;

        // Escuchamos cambios directamente en el documento de la póliza
        const docRef = doc(db, `public/data/clientes/${cId}/polizas`, pId);
        
        const unsubscribe = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                setLocalPoliza({ id: snap.id, path: snap.ref.path, ...snap.data() });
            }
        }, (error) => {
            console.error("Error en listener DetalleTab:", error);
        });

        return () => unsubscribe();
    }, [poliza?.id, poliza?.clienteId, client?.id]);

    // 2. VALIDACIÓN DE HISTORIAL PARA CAUCIONES
    useEffect(() => {
        const checkEndosos = async () => {
            if (!localPoliza?.riesgo?.toUpperCase().includes('CAUCI')) {
                setIsValidating(false);
                return;
            }

            if (!localPoliza?.id || !localPoliza?.clienteId) {
                setIsValidating(false);
                return;
            }

            try {
                const ref = collection(db, `public/data/clientes/${localPoliza.clienteId}/polizas/${localPoliza.id}/endosos`);
                const q = query(ref, where('numeroEndoso', '==', '0'), limit(1));
                const snap = await getDocs(q);
                setHasRefacturaciones(!snap.empty);
            } catch (e) { 
                console.error("Error validando historial:", e); 
            } finally {
                setIsValidating(false);
            }
        };
        checkEndosos();
    }, [localPoliza?.id, localPoliza?.clienteId, localPoliza?.riesgo]);

    // --- HELPERS DE FORMATO ---
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
        if (typeof dateString === 'string' && dateString.includes('-')) {
            const parts = dateString.split('-');
            if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
    };

    const formatMoney = (val) => {
        if (!val && val !== 0) return '0,00';
        let num = val;
        if (typeof val === 'string') {
             const cleanVal = val.replace(/[^0-9,.-]/g, '');
             num = parseFloat(cleanVal.replace(/\./g, '').replace(',', '.'));
        }
        if (isNaN(num)) return '0,00';
        return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    };

    // --- SUB-COMPONENTES DE UI ---
    const DetailItem = ({ label, value, highlight = false }) => (
        <div className="text-left">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
            <div className={`text-base font-medium break-words min-h-[1.5rem] ${highlight ? 'text-indigo-700 font-bold' : 'text-gray-800'}`}>
                {value || <span className="text-gray-300 italic font-normal text-sm">Sin dato</span>}
            </div>
        </div>
    );

    const CurrencyItem = ({ label, currency, amount, isNegative, colorClass = "text-blue-600" }) => (
        <div className="text-left">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
            <p className="text-base font-medium text-gray-800 break-words min-h-[1.5rem]">
                {(amount || amount === 0) ? (
                    <span>
                        <span className={`font-bold mr-1.5 ${isNegative ? 'text-red-600' : colorClass}`}>
                            {currency || '$'} {isNegative ? '-' : ''}
                        </span>
                        {formatMoney(amount)}
                    </span>
                ) : (
                    <span className="text-gray-300 italic text-sm">Sin dato</span>
                )}
            </p>
        </div>
    );

    const isAnulada = (localPoliza.estadoEmision || '').toUpperCase() === 'ANULADA';
    const isCaucionConHistorial = localPoliza.riesgo?.toUpperCase().includes('CAUCI') && hasRefacturaciones;
    const isAnulable = !isAnulada && !isCaucionConHistorial;

    // Validación para mostrar antecedentes de renovación
    const esPolizaRenovada = Boolean(
        localPoliza.polizaExpirante || 
        localPoliza.companiaExpirante || 
        localPoliza.ciaExpirante || 
        localPoliza.quienRenovo
    );

    const esNoRenueva = localPoliza.estadoGestionRenovacion === 'NO RENUEVA';

    return (
        <div className="space-y-6 animate-fade-in text-left">
            
            {/* BOTONES DE ACCIÓN */}
            {!isValidating && (
                <div className="flex justify-end gap-3 animate-fade-in">
                    <button 
                        onClick={() => setShowEditModal(true)}
                        disabled={isAnulada}
                        className={`flex items-center text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm border ${isAnulada ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100 hover:text-indigo-800'}`}
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>
                        Editar Póliza
                    </button>

                    {isAnulable && (
                        <button 
                            onClick={() => setShowAnulacionModal(true)}
                            className="flex items-center text-sm font-semibold px-4 py-2 rounded-full transition-colors shadow-sm border bg-red-50 text-red-600 border-red-100 hover:bg-red-100 hover:text-red-800"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                            Anular Póliza
                        </button>
                    )}
                </div>
            )}

            {/* SECCIÓN 0: DATOS DE ANULACIÓN */}
            {isAnulada && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 relative">
                     <div className="flex items-center mb-4 pb-2 border-b border-red-200">
                        <div className="p-2 bg-red-100 rounded-lg text-red-600 mr-3">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        </div>
                        <h3 className="text-lg font-bold text-red-800">Datos de Anulación</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <DetailItem label="Motivo Anulación" value={localPoliza.motivoAnulacion} />
                        <DetailItem label="Fecha Anulación" value={formatDate(localPoliza.fechaAnulacion)} />
                        <CurrencyItem label="Prima Anulación" currency={localPoliza.moneda} amount={localPoliza.primaAnulacion} isNegative />
                        <DetailItem label="¿Quién Gestionó?" value={localPoliza.quienGestiono} />
                        
                        <div className="md:col-span-4 mt-2">
                            <p className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2">Observaciones de la Anulación</p>
                            <div className="bg-white/60 p-4 rounded-xl border border-red-100 min-h-[4rem]">
                                <p className="text-sm text-red-900 whitespace-pre-wrap italic">
                                    {localPoliza.observacionesAnulacion || "Sin observaciones detalladas."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SECCIÓN 1: DETALLES DEL RIESGO */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center mb-6 pb-2 border-b border-gray-100">
                    <div className="p-2 bg-gray-100 rounded-lg text-gray-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Detalles del Riesgo</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <DetailItem label="Fecha de Carga" value={formatDate(localPoliza.fechaCarga)} />
                    <DetailItem label="N° de Póliza" value={localPoliza.nPoliza} highlight />
                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Tipo Operación</p>
                        <div className="mt-1">
                            {localPoliza.motivo ? (
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${getMotivoChipClass(localPoliza.motivo)}`}>
                                    {localPoliza.motivo}
                                </span>
                            ) : <span className="text-gray-300 italic text-sm">Sin dato</span>}
                        </div>
                    </div>
                    <DetailItem label="Sub-Operacion" value={localPoliza.subOperacion} />        
                    <DetailItem label="Inicio Vigencia" value={formatDate(localPoliza.inicioVigencia)} />
                    <DetailItem label="Fin Vigencia" value={formatDate(localPoliza.finVigencia)} />
                    <DetailItem label="Riesgo" value={localPoliza.riesgo} />
                    <DetailItem label="Subriesgo" value={localPoliza.subriesgo} />

                    {/* CAMPOS DINÁMICOS */}
                    {localPoliza.patentes?.filter(p => p !== '').length > 0 && (
                        <div className="md:col-span-1 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Patentes</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {localPoliza.patentes.filter(p => p !== '').map((p, i) => (
                                    <span key={i} className="bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded text-sm font-medium border border-yellow-200">{p}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {localPoliza.actividades?.filter(a => a !== '').length > 0 && (
                        <div className="md:col-span-2 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Actividades</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {localPoliza.actividades.filter(a => a !== '').map((a, i) => (
                                    <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-sm font-medium border border-blue-200">{a}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {localPoliza.especialidades?.filter(e => e !== '').length > 0 && (
                        <div className="md:col-span-2 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Especialidades</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {localPoliza.especialidades.filter(e => e !== '').map((e, i) => (
                                    <span key={i} className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-sm font-medium border border-indigo-200">{e}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {localPoliza.ubicacionesRiesgo?.filter(u => u !== '' && (typeof u === 'string' || (u.direccion && u.direccion.trim() !== ''))).length > 0 && (
                        <div className="md:col-span-3 bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Ubicaciones del Riesgo</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                                {localPoliza.ubicacionesRiesgo.map((u, i) => {
                                    if (typeof u === 'string' && u.trim() !== '') {
                                        return <li key={i}>{u}</li>;
                                    } else if (typeof u === 'object' && u.direccion?.trim() !== '') {
                                        return (
                                            <li key={i}>
                                                {u.direccion} 
                                                {u.m2 ? <span className="font-semibold text-gray-500 ml-1">({u.m2} m²)</span> : ''}
                                            </li>
                                        );
                                    }
                                    return null;
                                })}
                            </ul>
                        </div>
                    )}

                    {/* INDICADORES DE PÓLIZA */}
                    <div className="md:col-span-4 mt-4 pt-4 border-t border-gray-50">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Indicadores de Póliza</p>
                        <div className="flex flex-wrap gap-3">
                            {localPoliza.isPolizaPack && (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-orange-50 text-orange-800 border border-orange-200 shadow-sm uppercase tracking-tighter gap-1.5">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                    Póliza Pack: {localPoliza.referenciasPolizaPack?.join(", ") || 'S/D'}
                                </span>
                            )}

                            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border shadow-sm uppercase tracking-tighter gap-1.5 ${
                                localPoliza.formaRenovacion === 'AUTOMÁTICA' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                : 'bg-orange-50 text-orange-600 border-orange-200'
                            }`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" /></svg>
                                Forma de Renovación: {localPoliza.formaRenovacion || 'MANUAL'}
                            </span>

                            {localPoliza.tipoRenovacion && (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 shadow-sm uppercase tracking-tighter gap-1.5">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                                    Tipo de Renovación: {localPoliza.tipoRenovacion}
                                </span>
                            )}
                        </div>
                        {isCaucionConHistorial && (
                            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-2 animate-fade-in">
                                <svg className="w-4 h-4 text-orange-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <p className="font-bold text-orange-700 uppercase italic text-[10px] tracking-tight">Póliza con refacturaciones activas. La anulación debe gestionarse desde el historial de movimientos.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECCIÓN NUEVA: ORIGEN Y ANTECEDENTES DE RENOVACIÓN (SÓLO SI VIENE DE OTRA PÓLIZA) */}
            {esPolizaRenovada && (
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-slate-400"></div>
                    <div className="flex items-center mb-6 pb-2 border-b border-slate-200">
                        <div className="p-2 bg-slate-200 rounded-lg text-slate-700 mr-3">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">Origen y Antecedente de Renovación</h3>
                            <p className="text-xs text-slate-500 font-medium">Información de la póliza previa que dio origen a la emisión actual.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <DetailItem label="Quién Renovó" value={localPoliza.quienRenovo} />
                        <DetailItem label="Póliza Expirante" value={localPoliza.polizaExpirante} highlight />
                        <DetailItem label="Compañía Expirante" value={localPoliza.companiaExpirante || localPoliza.ciaExpirante} />
                    </div>
                </div>
            )}

            {/* SECCIÓN 2: DATOS DE LA COMPAÑÍA */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center mb-6 pb-2 border-b border-gray-100">
                    <div className="p-2 bg-gray-100 rounded-lg text-gray-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Datos de la Compañía</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <DetailItem label="Compañía" value={localPoliza.compania} highlight />
                    <DetailItem label="Organizador" value={localPoliza.organizador} />
                    <DetailItem label="PAS" value={localPoliza.pas} />
                </div>
            </div>

            {/* SECCIÓN 3: EQUIPO COMERCIAL (HÍBRIDO) */}
            <div className="bg-white p-6 rounded-xl border border-indigo-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-400"></div>
                <div className="flex items-center mb-6 pb-2 border-b border-indigo-100">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Equipo Comercial</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <DetailItem label="Tipo de Canal" value={localPoliza.canal} />
                    <DetailItem label="Nombre del Canal" value={localPoliza.canalNombre || localPoliza.subcanal} />
                    <DetailItem label="Subcanal Asociado" value={localPoliza.subcanalNombre} />
                    
                    <div className="col-span-1 md:col-span-3 border-t border-dashed border-indigo-100 my-2"></div>

                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Vendedor</p>
                        <div className="mt-1">
                             {localPoliza.vendedor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(localPoliza.vendedor)}`}>{localPoliza.vendedor}</span> : '-'}
                        </div>
                    </div>

                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Suscriptor</p>
                        <div className="mt-1">
                             {localPoliza.suscriptor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(localPoliza.suscriptor)}`}>{localPoliza.suscriptor}</span> : '-'}
                        </div>
                    </div>

                    <DetailItem label="Tipo de Conversión" value={localPoliza.tipoConversion} />
                    
                    <div className="col-span-1 md:col-span-3">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">¿Contador Asociado?</p>
                        {localPoliza.contadorNombre ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                {localPoliza.contadorNombre}
                            </span>
                        ) : (
                            <span className="text-gray-400 text-sm italic">No asignado</span>
                        )}
                    </div>
                </div>
            </div>

            {/* SECCIÓN 4: FACTURACIÓN Y VALORES */}
            <div className="bg-white p-6 rounded-xl border border-green-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-green-400"></div>
                <div className="flex items-center mb-6 pb-2 border-b border-green-100">
                    <div className="p-2 bg-green-100 rounded-lg text-green-600 mr-3">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Facturación y Valores</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <DetailItem label="Forma de Pago" value={localPoliza.formaPago} />
                    <DetailItem label="Tipo Facturación" value={localPoliza.tipoFacturacion} />
                    <DetailItem label="Cuotas" value={localPoliza.cuotas ? `${localPoliza.cuotas} Cuotas` : 'S/D'} />
                    <DetailItem label="Medio de Pago Actual" value={localPoliza.medioPago} />
                    <DetailItem label="Medio de pago de la Emisión" value={localPoliza.emisionPago} />
                    <DetailItem 
                        label="1er Pago efectuado" 
                        value={
                            (localPoliza.pagoEfectuado === true || 
                            String(localPoliza.pagoEfectuado).toUpperCase().trim() === 'SÍ' || 
                            String(localPoliza.pagoEfectuado).toUpperCase().trim() === 'SI') ? (
                                <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-700 select-none">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse"></span>
                                    Si
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border border-rose-100 bg-rose-50 text-rose-700 select-none">
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5 animate-pulse"></span>
                                    No
                                </span>
                            )
                        } 
                    />
                    <DetailItem label="¿Quién Cobró?" value={localPoliza.quienCobro} />
                    <DetailItem label="Fecha de Cobro" value={formatDate(localPoliza.fechaCobro)} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-gray-50">
                    <CurrencyItem label="Prima Emitida" currency={localPoliza.moneda} amount={localPoliza.primaEmitida} />
                    <CurrencyItem label="Premio" currency={localPoliza.moneda} amount={localPoliza.premio} />
                    
                    {localPoliza.primaAdelanto > 0 && (
                        <CurrencyItem label="Prima Adelanto" currency={localPoliza.moneda} amount={localPoliza.primaAdelanto} colorClass="text-emerald-600" />
                    )}
                    {localPoliza.primaPrimeraCuota > 0 && (
                        <CurrencyItem label="Prima Primera Cuota" currency={localPoliza.moneda} amount={localPoliza.primaPrimeraCuota} colorClass="text-indigo-600" />
                    )}
                    
                    <DetailItem label="% Comisión Total" value={localPoliza.comision ? `${localPoliza.comision}%` : '0%'} />
                </div>
            </div>

            {/* SECCIÓN 6: GESTIÓN INTERNA DE RENOVACIÓN (ACTIVA) */}
            <div className="bg-white p-6 rounded-xl border border-orange-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1.5 h-full bg-orange-400"></div>
                
                {/* CABECERA PRINCIPAL */}
                <div className="flex items-center justify-between mb-6 pb-2 border-b border-orange-100">
                    <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg text-orange-600 mr-3">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Gestión de Renovación</h3>
                            <p className="text-xs text-gray-500 font-medium">Estado y seguimiento del próximo vencimiento de esta póliza.</p>
                        </div>
                    </div>
                    {/* BOTÓN PARA EDITAR LA RENOVACIÓN */}
                    <button 
                        onClick={() => setShowEditarRenovacionModal(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-transparent hover:border-orange-200 rounded-lg transition-all"
                        title="Editar estado de renovación"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path>
                        </svg>
                        Modificar Estado
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Grid adaptativo: 3 columnas si es NO RENUEVA para intercalar el motivo */}
                    <div className={`grid grid-cols-1 ${esNoRenueva ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                        <DetailItem label="Estado de gestión" value={localPoliza.estadoGestionRenovacion} />
                        
                        {/* CAMPO DINÁMICO: Motivo visible solo si es NO RENUEVA */}
                        {esNoRenueva && (
                            <div className="animate-fade-in">
                                <DetailItem 
                                    label="Motivo No Renueva" 
                                    value={
                                        localPoliza.motivoNoRenueva ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                                {localPoliza.motivoNoRenueva}
                                            </span>
                                        ) : null
                                    } 
                                />
                            </div>
                        )}

                        <DetailItem label="Responsable de la Gestión" value={localPoliza.gestionadoPor} />
                        
                        {/* Observaciones a ancho completo */}
                        <div className={esNoRenueva ? 'md:col-span-3' : 'md:col-span-2'}>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Observaciones de Renovación</p>
                            <div className="bg-orange-50/30 p-4 rounded-xl border border-orange-100 min-h-[4.5rem]">
                                <p className="text-sm text-orange-900 whitespace-pre-wrap">
                                    {localPoliza.observacionesRenovacion || <span className="text-gray-300 italic">Sin observaciones registradas en la gestión.</span>}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MODALES ANIDADOS */}
            <ModalEmision 
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                clientData={client}
                initialData={localPoliza}
                onSaveSuccess={(msg) => {
                    if (onUpdate) onUpdate(msg);
                    setShowEditModal(false);
                }}
            />
            <ModalAnulacion
                show={showAnulacionModal}
                onClose={() => setShowAnulacionModal(false)}
                poliza={localPoliza}
                clientId={client?.id}
                onSuccess={(msg) => {
                    if (onUpdate) onUpdate(msg);
                }}
            />

            {/* MODAL DE EDICIÓN DE RENOVACIÓN */}
            {showEditarRenovacionModal && (
                <EditarRenovacion 
                    show={showEditarRenovacionModal}
                    onClose={() => setShowEditarRenovacionModal(false)}
                    poliza={{
                        ...localPoliza,
                        path: localPoliza.path || `public/data/clientes/${client?.id || localPoliza.clienteId}/polizas/${localPoliza.id}`
                    }}
                    freshClientData={client}
                    onSuccess={(msg) => {
                        if (onUpdate) onUpdate(msg);
                        setShowEditarRenovacionModal(false);
                    }}
                />
            )}

            <style>{`.animate-fade-in { animation: fadeIn 0.3s ease-out forwards; } @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
    );
};

export default DetalleTab;